/**
 * Clip Utilities
 * 
 * Helper functions for video editor clip operations.
 */

import { EditorClip, SourceEventClip, ExportData } from '../types';

/**
 * Generate a unique clip ID
 */
export function generateClipId(): string {
  return `clip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Calculate total duration of all clips
 */
export function calculateTotalDuration(clips: EditorClip[]): number {
  return clips.reduce((total, clip) => total + (clip.endTime - clip.startTime), 0);
}

/**
 * Get clip duration in seconds
 */
export function getClipDuration(clip: EditorClip): number {
  return clip.endTime - clip.startTime;
}

/**
 * Format seconds to MM:SS or HH:MM:SS
 */
export function formatTime(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format seconds to MM:SS.ms (with milliseconds)
 */
export function formatTimePrecise(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);

  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}

/**
 * Convert event clip to editor clip
 */
export function eventClipToEditorClip(
  eventClip: SourceEventClip,
  rangeBefore: number = 5,
  rangeAfter: number = 5
): EditorClip {
  return {
    id: generateClipId(),
    sourceVideoId: eventClip.videoId,
    sourceVideoUrl: eventClip.videoUrl,
    startTime: Math.max(0, eventClip.markerTime - rangeBefore),
    endTime: eventClip.markerTime + rangeAfter,
    markerTime: eventClip.markerTime,
    label: eventClip.label,
    thumbnailUrl: eventClip.thumbnailUrl,
    sourceType: 'event_clip',
    sourceTagId: eventClip.id,
    metadata: eventClip.metadata,
  };
}

/**
 * Get a numeric key used for sorting clips (match/order time)
 */
export function getClipSortKey(clip: EditorClip): number {
  if (typeof clip.markerTime === 'number') {
    return clip.markerTime;
  }
  if (typeof clip.metadata?.matchMinute === 'number') {
    return clip.metadata.matchMinute * 60;
  }
  return clip.startTime;
}

/**
 * Get the clip and position within clip for a given timeline position
 */
export function getClipAtPosition(
  clips: EditorClip[],
  timelinePosition: number
): { clip: EditorClip; positionInClip: number; clipIndex: number } | null {
  let accumulatedTime = 0;

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];
    const clipDuration = clip.endTime - clip.startTime;

    if (timelinePosition < accumulatedTime + clipDuration) {
      return {
        clip,
        positionInClip: clip.startTime + (timelinePosition - accumulatedTime),
        clipIndex: i,
      };
    }

    accumulatedTime += clipDuration;
  }

  return null;
}

/**
 * Get timeline position for start of a specific clip
 */
export function getClipStartPosition(clips: EditorClip[], clipId: string): number {
  let position = 0;

  for (const clip of clips) {
    if (clip.id === clipId) {
      return position;
    }
    position += clip.endTime - clip.startTime;
  }

  return position;
}

/**
 * Validate clip times
 */
export function validateClipTimes(
  startTime: number,
  endTime: number,
  minDuration: number = 0.5
): { valid: boolean; error?: string } {
  if (startTime < 0) {
    return { valid: false, error: 'Start time cannot be negative' };
  }
  if (endTime <= startTime) {
    return { valid: false, error: 'End time must be after start time' };
  }
  if (endTime - startTime < minDuration) {
    return { valid: false, error: `Clip must be at least ${minDuration} seconds` };
  }
  return { valid: true };
}

/**
 * Prepare clips for export (convert to export format)
 */
export function prepareExportData(
  clips: EditorClip[],
  matchId?: string,
  options?: {
    format?: 'mp4' | 'webm';
    quality?: 'original' | 'high' | 'medium' | 'low';
    resolution?: '1080p' | '720p' | '480p';
    title?: string;
    description?: string;
  }
): ExportData {
  return {
    matchId,
    clips: clips.map((clip, index) => ({
      sourceVideoUrl: clip.sourceVideoUrl,
      startTime: clip.startTime,
      endTime: clip.endTime,
      order: index,
    })),
    output: {
      format: options?.format || 'mp4',
      quality: options?.quality || 'high',
      resolution: options?.resolution,
    },
    metadata: {
      title: options?.title,
      description: options?.description,
    },
  };
}

/**
 * Calculate pixels per second based on zoom level and container width
 */
export function calculatePixelsPerSecond(
  containerWidth: number,
  totalDuration: number,
  zoomLevel: number,
  minPixelsPerSecond: number = 10
): number {
  const basePixelsPerSecond = containerWidth / Math.max(totalDuration, 1);
  return Math.max(basePixelsPerSecond * zoomLevel, minPixelsPerSecond);
}

/**
 * Snap time to nearest frame (assuming 30fps)
 */
export function snapToFrame(time: number, fps: number = 30): number {
  return Math.round(time * fps) / fps;
}

/**
 * Check if two clips overlap in source video (for merging)
 */
export function clipsOverlap(clip1: EditorClip, clip2: EditorClip): boolean {
  if (clip1.sourceVideoId !== clip2.sourceVideoId) return false;
  return clip1.startTime < clip2.endTime && clip2.startTime < clip1.endTime;
}

/**
 * Merge overlapping clips from the same source
 */
export function mergeOverlappingClips(clip1: EditorClip, clip2: EditorClip): EditorClip | null {
  if (!clipsOverlap(clip1, clip2)) return null;

  return {
    ...clip1,
    id: generateClipId(),
    startTime: Math.min(clip1.startTime, clip2.startTime),
    endTime: Math.max(clip1.endTime, clip2.endTime),
    label: clip1.label || clip2.label,
  };
}

